import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRepo } from '../app/services';
import type { ClubAuditFilter, EventFilter } from './repository';

export const qk = {
  orgs: ['organizations'] as const,
  org: (id: string) => ['organization', id] as const,
  club: (id: string, kind: string) => ['club', id, kind] as const,
  platform: (kind: string) => ['platform', kind] as const,
};

export function useOrganizations() {
  const repo = useRepo();
  return useQuery({ queryKey: qk.orgs, queryFn: () => repo.listOrganizations() });
}

export function useOrganization(orgId: string) {
  const repo = useRepo();
  return useQuery({ queryKey: qk.org(orgId), queryFn: () => repo.getOrganization(orgId) });
}

function useClubQuery<T>(orgId: string, kind: string, fn: () => Promise<T>) {
  return useQuery({ queryKey: qk.club(orgId, kind), queryFn: fn });
}

export const useLocations = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'locations', () => r.listLocations(orgId));
};
export const useBuildings = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'buildings', () => r.listBuildings(orgId));
};
export const useDoors = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'doors', () => r.listDoors(orgId));
};
export const useLocks = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'locks', () => r.listLocks(orgId));
};
export const usePersons = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'persons', () => r.listPersons(orgId));
};
export const useFaqItems = () => {
  const r = useRepo();
  return useQuery({ queryKey: ['faq_items'], queryFn: () => r.listFaqItems(), staleTime: 5 * 60_000 });
};
export const useLoanCards = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'loan_cards', () => r.listLoanCards(orgId));
};
export const useLoanCardHistory = (orgId: string, personId: string, enabled = true) => {
  const r = useRepo();
  return useClubQuery(orgId, `loan_history:${personId}`, () => (enabled ? r.loanCardHistory(personId) : Promise.resolve([])));
};
export const useClubInstallers = (orgId: string, enabled = true) => {
  const r = useRepo();
  return useClubQuery(orgId, 'club_installers', () => (enabled ? r.listClubInstallers(orgId) : Promise.resolve([])));
};
export const usePersonAppStatus = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'person_app_status', () => r.listPersonAppStatus(orgId));
};
export const useGroups = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'groups', () => r.listGroups(orgId));
};
export const useGroupMembers = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'group_members', () => r.listGroupMembers(orgId));
};
export const useGroupManagers = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'group_managers', () => r.listGroupManagers(orgId));
};
export const useAccessRules = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'access_rules', () => r.listAccessRules(orgId));
};
export const useCredentials = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'credentials', () => r.listCredentials(orgId));
};
export const useLockGrants = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'lock_grants', () => r.listLockGrants(orgId));
};
export const useLockHardening = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'lock_hardening', () => r.listLockHardening(orgId));
};
export const useSyncJobs = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'sync_jobs', () => r.listSyncJobs(orgId));
};
export const useAccessCodes = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'access_codes', () => r.listAccessCodes(orgId));
};
export const useMemberships = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'memberships', () => r.listMemberships(orgId));
};
export const useClubAdmins = (orgId: string) => {
  const r = useRepo();
  return useClubQuery(orgId, 'club_admins', () => r.listClubAdmins(orgId));
};
export const usePlatformStaff = () => {
  const r = useRepo();
  return useQuery({ queryKey: qk.platform('staff'), queryFn: () => r.listPlatformStaff() });
};
export const useLockPlacements = () => {
  const r = useRepo();
  return useQuery({ queryKey: qk.platform('lock_placements'), queryFn: () => r.listLockPlacements() });
};
export const useAccessEvents =(orgId: string, filter: EventFilter = {}) => {
  const r = useRepo();
  return useQuery({ queryKey: [...qk.club(orgId, 'access_events'), filter], queryFn: () => r.listAccessEvents(orgId, filter) });
};
export const useAuditLog = (orgId: string | null) => {
  const r = useRepo();
  return useQuery({ queryKey: ['audit', orgId], queryFn: () => r.listAuditLog(orgId) });
};

/** Beheerlogboek (RPC club_audit_log); invalideert mee met elke clubmutatie via de 'club'-sleutel. */
export const useClubAudit = (orgId: string, filter: ClubAuditFilter, enabled = true) => {
  const r = useRepo();
  return useQuery({
    queryKey: [...qk.club(orgId, 'club_audit'), filter],
    queryFn: () => r.listClubAudit(orgId, filter),
    enabled,
    placeholderData: (prev) => prev,
  });
};

/** Mutation that invalidates everything for the club (derived state changes widely). */
export function useClubMutation<TVars, TRes>(orgId: string, fn: (vars: TVars) => Promise<TRes>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['club', orgId] });
      void qc.invalidateQueries({ queryKey: ['audit'] });
      void qc.invalidateQueries({ queryKey: qk.org(orgId) });
      // Platformoverzichten (voorraad: welk slot op welke deur, clubdetail) tonen dezelfde data.
      void qc.invalidateQueries({ queryKey: ['platform'] });
    },
  });
}

export function usePlatformMutation<TVars, TRes>(fn: (vars: TVars) => Promise<TRes>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => void qc.invalidateQueries(),
  });
}
